// Terms & Conditions Modal Component
import React, { useEffect } from 'react';
import { MINIMUM_ORDER_AMOUNT_TEXT } from '../shared/constants';
import './TermsModal.css';

interface TermsModalProps {
    isOpen: boolean;
    onClose: () => void;
}

const TermsModal: React.FC<TermsModalProps> = ({ isOpen, onClose }) => {
    // Handle Escape key to close modal
    useEffect(() => {
        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && isOpen) {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener('keydown', handleEscape);
            // Prevent background scrolling when modal is open
            document.body.style.overflow = 'hidden';
        }

        return () => {
            document.removeEventListener('keydown', handleEscape);
            document.body.style.overflow = 'unset';
        };
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    return (
        <div className="terms-modal-overlay" onClick={onClose}>
            <div className="terms-modal-content" onClick={(e) => e.stopPropagation()}>
                <button className="terms-modal-close" onClick={onClose} aria-label="Close">
                    ✕
                </button>

                <div className="terms-modal-body">
                    <h1>Terms & Conditions – Deli Feed</h1>
                    <p className="last-updated">Last updated: November 2025</p>

                    <div className="terms-intro">
                        <p>
                            Welcome to Deli Feed ("we", "us", or "our").
                            These Terms govern how you use our website and order food from us.
                            By placing an order or using our website, you agree to these Terms.
                        </p>
                    </div>

                    <section>
                        <h2>1. About Us</h2>
                        <p>
                            We are a small independent food business providing freshly prepared meals for local delivery in the Cambourne area of Cambridge, UK.
                        </p>
                        <p className="contact-info">
                            📱 Contact us on WhatsApp or mobile: <strong>079 444 88 611</strong>
                        </p>
                    </section>

                    <section>
                        <h2>2. Orders and Payments</h2>
                        <p>
                            Orders may be placed through our website or WhatsApp.
                            Please check all details carefully before confirming.
                            Once confirmed, orders cannot be cancelled, as preparation begins immediately.
                        </p>

                        <p><strong>Payments accepted:</strong></p>
                        <ul>
                            <li>Card online (via Stripe)</li>
                            <li>Card on delivery (via SumUp)</li>
                            <li>Cash on delivery</li>
                        </ul>

                        <p>
                            A minimum order value of {MINIMUM_ORDER_AMOUNT_TEXT} (after discounts) applies.
                            Prices may change at any time without notice.
                            We are not VAT-registered.
                        </p>
                    </section>

                    <section>
                        <h2>3. Delivery</h2>
                        <p>
                            We deliver locally within our service area as displayed at checkout.
                            Delivery times are estimates only and may vary with traffic, weather or order volume.
                            Times are not guaranteed.
                            If we cannot deliver because of incorrect details or inability to contact you, the order may be cancelled without refund.
                        </p>
                    </section>

                    <section>
                        <h2>4. Refunds and Issues</h2>
                        <p>
                            If something appears wrong with your order, please contact us promptly.
                            Refunds or replacements may be offered at our sole discretion.
                            Our liability for any claim will not exceed the amount paid for that order.
                            We are not liable for indirect or consequential losses.
                        </p>
                    </section>

                    <section>
                        <h2>5. Allergens & Food Safety Policy</h2>

                        <h3>Allergens</h3>
                        <p>
                            Our food is prepared in a kitchen that may handle gluten, nuts, dairy, soy, eggs, sesame and other allergens.
                            We cannot guarantee allergen-free products.
                            If you have allergies or dietary requirements, please contact us before ordering for guidance.
                        </p>

                        <h3>Food Safety</h3>
                        <p>
                            We follow the requirements of the Food Safety and Hygiene (England) Regulations 2013 and general UK food-safety best practice for safe storage, handling and delivery.
                            Once delivered, food should be consumed promptly and stored appropriately.
                            We are not responsible for food safety once an order has been handed over.
                        </p>
                    </section>

                    <section>
                        <h2>6. Website Use and Content</h2>
                        <p>
                            Our website and its content are provided for personal, non-commercial use.
                            All text, images and branding belong to Deli Feed and may not be reused without permission.
                            We do not guarantee the website will be error-free or continuously available.
                        </p>
                    </section>

                    <section>
                        <h2>7. Liability Limitation</h2>
                        <p>
                            To the fullest extent permitted by law, we exclude all warranties, representations and conditions relating to this website or its use.
                            Our maximum liability to you is limited to the value of your order.
                        </p>
                    </section>

                    <section>
                        <h2>8. Changes to These Terms</h2>
                        <p>
                            We may update these Terms at any time by posting a new version on our website.
                            The updated version takes effect immediately upon publication.
                        </p>
                    </section>

                    <section>
                        <h2>9. Governing Law</h2>
                        <p>
                            These Terms are governed by the laws of England and Wales, and any dispute will be handled by the English courts.
                        </p>
                    </section>
                </div>
            </div>
        </div>
    );
};

export default TermsModal;
